'use client'

import React, { useState, useEffect } from 'react'
import classes from './index.module.css'
import { useAnimation } from '@/providers/Animation'

interface RainLineProps {
  delay: number
  duration: number
  left: string
  height: string
  opacity: number
}

const RainLine: React.FC<RainLineProps> = ({ delay, duration, left, height, opacity }) => {
  return (
    <div
      className={classes.rainLine}
      style={{
        left,
        height,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
        opacity,
      }}
    >
      <div className={classes.rainLineGradient} />
    </div>
  )
}

interface RainEffectProps {
  lineCount?: number
  className?: string
}

// Generate rain lines with random properties (client-side only)
function generateRainLines(lineCount: number): RainLineProps[] {
  const result: RainLineProps[] = []

  for (let i = 0; i < lineCount; i++) {
    // Distribute lines evenly with some randomness
    const basePosition = (i / lineCount) * 100
    const randomOffset = (Math.random() - 0.5) * 5
    const left = `${Math.max(0, Math.min(100, basePosition + randomOffset))}%`

    // Vary the properties for natural look
    const heightVariant = Math.random()
    let height: string
    if (heightVariant < 0.3) {
      height = '40%' // tiny
    } else if (heightVariant < 0.5) {
      height = '50%' // small
    } else if (heightVariant < 0.75) {
      height = '65%' // medium
    } else {
      height = '100%' // full
    }

    result.push({
      delay: Math.random() * 8, // 0-8s delay
      duration: 6 + Math.random() * 4, // 6-10s duration (slower)
      left,
      height,
      opacity: 0.3 + Math.random() * 0.7, // 0.3-1.0 opacity
    })
  }

  return result
}

export const RainEffect: React.FC<RainEffectProps> = ({ lineCount = 20, className = '' }) => {
  const { animationsEnabled } = useAnimation()
  const [lines, setLines] = useState<RainLineProps[]>([])
  const [isClient, setIsClient] = useState(false)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mq.matches)
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // Only generate random lines on client after hydration
  useEffect(() => {
    setIsClient(true)
    setLines(generateRainLines(lineCount))
  }, [lineCount])

  // Don't render until client-side to avoid hydration mismatch
  if (!isClient || !animationsEnabled || prefersReducedMotion) {
    return null
  }

  return (
    <div className={`${classes.rainWrapper} ${className}`}>
      {lines.map((line, index) => (
        <RainLine key={index} {...line} />
      ))}
    </div>
  )
}

export default RainEffect
